import React from 'react';
import { FeatureFlags, PeriodBehavior } from '@fabric-ui/components';
import { TimeBucket } from '@fabric-ui/components/Table';
import {
  Bars,
  Donut,
  HorizontalBars,
  HorizontalBarsStacked,
  HorizontalBarsStacked100,
  Line,
  Line2,
  Pie,
  PivotTable,
  RichText,
  ChartTile as StatusBar,
  Table,
  TrendTile,
  VerticalBarsStacked,
  VerticalBarsStacked100,
} from '@fabric-ui/icons/widgetTypes';
import { hasTimeBucket } from './EditCustomDashboard/TimeBucketBreakdown/utils';
import {
  AllTypeWidgets,
  BarLegendPosition,
  BarSubType,
  BarVerticalTypeWidget,
  ColoringRules,
  DisplayModeOptions,
  dwQueryDefault,
  Field,
  LineLegendPosition,
  LineSubType,
  LineWidget,
  PieBasicLegendPosition,
  PieLegendStyles,
  PieSubType,
  PieWidget,
  TableRowSize,
  TableSubType,
  TextCategoryWidgetTypes,
  TextSubType,
  TileSubType,
  TileTypeWidget,
  TileWidgetVisualization,
  WidgetCategory,
  WidgetDefinition,
} from './types';
import { AxisTickType, SizeLimit } from './types/types';

export const NO_VALUE = '—';
export const EMPTY_STRING = '" "';
export const NullValues = ['null', null, NO_VALUE, EMPTY_STRING];

export const OTHERS = 'Others';

export enum DimensionTypes {
  Row = 'Row',
  Col = 'Col',
}

const widgetIconStyle = { width: 33, height: 28 };

export const defaultDefinition: WidgetDefinition = {
  title: {
    title: '',
  },
  coords: {
    x: 0,
    y: Infinity,
    h: 4,
    w: 6,
  },
};
export const defaultText: TextCategoryWidgetTypes = {
  id: '',
  definition: { ...defaultDefinition, custom: { text: '' } },
  category: WidgetCategory.Text,
  type: TextSubType.Text,
  requests: [dwQueryDefault],
};
export const defaultDrillDownHierarchy = {
  activeIndex: 0,
  fields: [],
  drillDownValues: [],
  notOthersDrillDownValues: [],
};
export const defaultBar: BarVerticalTypeWidget = {
  id: '',
  definition: {
    ...defaultDefinition,
    custom: {
      legend: { position: BarLegendPosition.Top },
      rowsColumnSwitch: false,
      fieldsPalette: {},
      displayMode: DisplayModeOptions.FIT,
      xAxisTickType: AxisTickType.Dynamic,
      yAxisTickType: AxisTickType.Dynamic,
    },
  },
  category: WidgetCategory.Bar,
  type: BarSubType.BarVertical,
  requests: [dwQueryDefault],
  drillDownHierarchy: defaultDrillDownHierarchy,
};
export const defaultLine: LineWidget = {
  id: '',
  definition: {
    ...defaultDefinition,
    custom: { fieldsPalette: {}, legend: { position: LineLegendPosition.Top } },
  },
  category: WidgetCategory.Line,
  type: LineSubType.Line,
  requests: [dwQueryDefault],
  drillDownHierarchy: defaultDrillDownHierarchy,
};
export const defaultTile: TileTypeWidget = {
  id: '',
  definition: {
    ...defaultDefinition,
    coords: {
      x: 0,
      y: Infinity,
      h: 3,
      w: 4,
    },
    custom: {
      visualization: TileWidgetVisualization.None,
      coloringRule: ColoringRules.UP_IS_GOOD,
    },
  },
  category: WidgetCategory.Tile,
  type: TileSubType.Tile,
  requests: [dwQueryDefault],
};
export const defaultTrend: TileTypeWidget = {
  id: '',
  definition: {
    ...defaultDefinition,
    coords: {
      x: 0,
      y: Infinity,
      h: 3,
      w: 4,
    },
    custom: {
      visualization: TileWidgetVisualization.None,
      coloringRule: ColoringRules.DOWN_IS_GOOD,
      showChangeIndicator: true,
    },
  },
  category: WidgetCategory.Tile,
  type: TileSubType.Tile,
  requests: [
    {
      ...dwQueryDefault,
      groupBy: [TimeBucket.BUCKET_DAY],
      timeRange: {
        relative: {
          value: 30,
          unit: 'DAYS',
          periodBehaviour: PeriodBehavior.last,
        },
      },
      select: { ...dwQueryDefault.select, dims: [{ name: TimeBucket.BUCKET_DAY }] },
    },
  ],
};

export const defaultWidget: AllTypeWidgets = {
  id: '',
  definition: { ...defaultDefinition, custom: { rowSize: TableRowSize.Small, pageSize: 25 } },
  category: WidgetCategory.Table,
  drillDownHierarchy: defaultDrillDownHierarchy,
  type: TableSubType.Table,
  requests: [dwQueryDefault],
};

export const defaultPivotTable = {
  ...defaultWidget,
  type: TableSubType.PivotTable,
};
export const tableDefaultSize = 20;
export const defaultTable = () => ({
  ...defaultWidget,
  requests: defaultWidget.requests.map(r => ({
    ...r,
    ...(r.top
      ? {
          top: {
            ...r.top,
            size: undefined,
          },
        }
      : undefined),
  })),
});
export const defaultPie = (type: PieSubType = PieSubType.Pie): PieWidget => ({
  id: '',
  definition: {
    ...defaultDefinition,
    custom: {
      ...(type === PieSubType.Donut && { showTotals: true }),
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: true,
        showPercentage: true,
      },

      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type,
  requests: [dwQueryDefault],
  drillDownHierarchy: defaultDrillDownHierarchy,
});
export const widgetTypeObject = {
  [WidgetCategory.Bar]: {
    [BarSubType.BarVertical]: {
      value: WidgetCategory.Bar,
      title: 'Vertical Bars',
      icon: <Bars style={widgetIconStyle} />,
      default: defaultBar,
    },
    [BarSubType.BarHorizontal]: {
      value: WidgetCategory.Bar,
      title: 'Horizontal Bars',
      icon: <HorizontalBars style={widgetIconStyle} />,
      default: defaultBar,
    },
    [BarSubType.StackedVertical]: {
      value: WidgetCategory.Bar,
      title: 'Vertical Bars Stacked',
      icon: <VerticalBarsStacked style={widgetIconStyle} />,
      default: defaultBar,
    },
    [BarSubType.StackedHorizontal]: {
      value: WidgetCategory.Bar,
      title: 'Horizontal Bars Stacked',
      icon: <HorizontalBarsStacked style={widgetIconStyle} />,
      default: defaultBar,
    },
    [BarSubType.Percentage100_Vertical]: {
      value: WidgetCategory.Bar,
      title: 'Vertical Bars Stacked 100%',
      icon: <VerticalBarsStacked100 style={widgetIconStyle} />,
      default: defaultBar,
    },
    [BarSubType.Percentage100_Horizontal]: {
      value: WidgetCategory.Bar,
      title: 'Horizontal Bars Stacked 100%',
      icon: <HorizontalBarsStacked100 style={widgetIconStyle} />,
      default: defaultBar,
    },
  },
  [WidgetCategory.Line]: {
    [LineSubType.Line]: {
      value: WidgetCategory.Line,
      title: 'Line',
      icon: <Line2 style={widgetIconStyle} />,
      default: defaultLine,
    },
    [LineSubType.Spline]: {
      value: WidgetCategory.Line,
      title: 'Spline',
      icon: <Line style={widgetIconStyle} />,
      default: defaultLine,
    },
  },
  [WidgetCategory.Pie]: {
    [PieSubType.Pie]: {
      value: WidgetCategory.Pie,
      title: 'Pie',
      icon: <Pie style={widgetIconStyle} />,
      default: defaultPie(),
    },
    [PieSubType.Donut]: {
      value: WidgetCategory.Pie,
      title: 'Donut',
      icon: <Donut style={widgetIconStyle} />,
      default: defaultPie(PieSubType.Donut),
    },
  },
  [WidgetCategory.Table]: {
    [TableSubType.Table]: {
      value: WidgetCategory.Table,
      title: 'Table',
      icon: <Table style={widgetIconStyle} />,
      default: defaultTable(),
    },
    [TableSubType.PivotTable]: {
      value: WidgetCategory.Table,
      title: 'Pivot Table',
      icon: <PivotTable style={widgetIconStyle} />,
      default: defaultPivotTable,
      featureFlag: FeatureFlags.PivotTable,
    },
  },
  [WidgetCategory.Tile]: {
    [TileSubType.Tile]: {
      value: WidgetCategory.Tile,
      title: 'Tile',
      icon: <StatusBar style={widgetIconStyle} />,
      default: defaultTile,
    },
    [TileSubType.Trend]: {
      value: WidgetCategory.Tile,
      title: 'Trend',
      icon: <TrendTile style={widgetIconStyle} />,
      default: defaultTrend,
    },
  },
  [WidgetCategory.Text]: {
    [TextSubType.Text]: {
      value: WidgetCategory.Text,
      title: 'Text',
      icon: <RichText style={widgetIconStyle} />,
      default: defaultText,
    },
  },
};
export const chartsDefaultSize = 100;

export enum Orientation {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

export const widgetTempIdInit = 'temp_widget_';
export const PeriodBehaviorCapitelized = {
  [PeriodBehavior.last]: 'Last',
  [PeriodBehavior.previous]: 'Previous',
};

export enum StackedType {
  None,
  Stacked,
  Stacked100,
}

export enum OrientationType {
  Vertical,
  Horizontal,
}

export const orientationOption = {
  [BarSubType.BarHorizontal]: OrientationType.Horizontal,
  [BarSubType.StackedHorizontal]: OrientationType.Horizontal,
  [BarSubType.Percentage100_Horizontal]: OrientationType.Horizontal,
  [BarSubType.BarVertical]: OrientationType.Vertical,
  [BarSubType.StackedVertical]: OrientationType.Vertical,
  [BarSubType.Percentage100_Vertical]: OrientationType.Vertical,
};
export const stackedOption = {
  [BarSubType.BarVertical]: StackedType.None,
  [BarSubType.BarHorizontal]: StackedType.None,
  [BarSubType.StackedVertical]: StackedType.Stacked,
  [BarSubType.StackedHorizontal]: StackedType.Stacked,
  [BarSubType.Percentage100_Vertical]: StackedType.Stacked100,
  [BarSubType.Percentage100_Horizontal]: StackedType.Stacked100,
};
export const loadingDashboardTempLayout = [
  {
    i: 'a',
    x: 0,
    y: 0,
    w: 8,
    h: 6,
    isResizable: false,
    isDraggable: false,
  },
  {
    i: 'b',
    x: 8,
    y: 0,
    w: 4,
    h: 6,
    isResizable: false,
    isDraggable: false,
  },
  {
    i: 'c',
    x: 0,
    y: 6,
    w: 4,
    h: 4,
    isResizable: false,
    isDraggable: false,
  },
  {
    i: 'd',
    x: 4,
    y: 6,
    w: 4,
    h: 4,
    isResizable: false,
    isDraggable: false,
  },
  {
    i: 'e',
    x: 8,
    y: 6,
    w: 4,
    h: 4,
    isResizable: false,
    isDraggable: false,
  },
];

const common: SizeLimit = { minW: 4, maxW: 12, minH: 4, maxH: 8 };

export const sizeLimitsPerCategory: Record<WidgetCategory, SizeLimit> = {
  [WidgetCategory.Pie]: common,
  [WidgetCategory.Bar]: common,
  [WidgetCategory.Line]: common,
  [WidgetCategory.Table]: { ...common, maxH: 13 },
  [WidgetCategory.Text]: { ...common, minH: 1, minW: 2 },
  [WidgetCategory.Tile]: { minH: 2, maxH: 4, minW: 2, maxW: 6 },
  [WidgetCategory.Venn]: common,
};

export enum ModeType {
  Edit,
  Create,
  SaveAs,
  Share,
}

export const noOverlap = 'No overlap';
export type TooltipDataType = {
  [noOverlap]: { value: number; percentage?: string; fill?: string };
  overlaps: Record<string, { value: number; percentage: string; fill?: string }>;
};
export const vennTheme = { base: 'custom-venn-tooltip', disablePointer: '' };

export interface FieldToCheck {
  repeated?: boolean;
  value: string;
  group?: string;
  id?: string;
}

export type LimitFnParams = { selectedDims?: Field[]; selectedMetrics?: Field[] };
export type LimitFn = (p: LimitFnParams) => number;

export type CustomValidationParams = { selectedDims: Field[]; field: FieldToCheck };
export type CustomValidationFn = (p: CustomValidationParams) => string | null;

export type LimitState = {
  isReached: boolean;
  message: string;
};

export type GetDimensionLimitStateFn = (p: { selectedDims: Field[] }) => LimitState;

export interface LimitConfig {
  metrics: LimitFn;
  dims: LimitFn;
  customDimensionValidation?: CustomValidationFn;
  getDimensionLimitState?: GetDimensionLimitStateFn;
}

const getDimsDisabledText = (limit: number) => `You can add up to ${limit} dimensions`;

export const widgetLimitConfigs: Partial<Record<string, LimitConfig>> = {
  [TableSubType.PivotTable]: {
    metrics: () => 5,
    dims: () => 2,
  },
  [WidgetCategory.Pie]: {
    metrics: () => 1,
    dims: () => 1,
  },
  [WidgetCategory.Bar]: {
    metrics: ({ selectedDims = [] }) => (selectedDims.length >= 2 ? 1 : 5),
    dims: ({ selectedMetrics = [] }) => (selectedMetrics.length >= 2 ? 1 : 2),
  },
  [WidgetCategory.Line]: {
    metrics: () => 5,
    dims: () => 2,
    customDimensionValidation: ({ selectedDims, field }) => {
      if (!field.id) return null;
      if (hasTimeBucket(field.id)) {
        if (selectedDims.filter(d => hasTimeBucket(d.name)).length >= 1) {
          return 'You can add up to 1 time-based dimension';
        }
      }
      if (!hasTimeBucket(field.id) && selectedDims.filter(d => !hasTimeBucket(d.name)).length >= 1) {
        return 'You can add up to 1 non-time-based dimension';
      }
      return null;
    },
    getDimensionLimitState: ({ selectedDims }) => {
      const selectedTimeBucketDims = selectedDims.filter(({ name }) => hasTimeBucket(name));
      const nonTimeBucketSelectedDims = selectedDims.filter(({ name }) => !hasTimeBucket(name));
      return {
        isReached: selectedTimeBucketDims.length >= 1 && nonTimeBucketSelectedDims.length >= 1,
        message: 'You can add up to 1 time-based and 1 non-time-based dimension',
      };
    },
  },
  [TileSubType.Trend]: {
    metrics: () => 1,
    dims: () => 1,
    customDimensionValidation: ({ field }) => {
      if (field.id && !hasTimeBucket(field.id)) {
        return 'Only time-based dimensions are allowed';
      }
      return null;
    },
    getDimensionLimitState: ({ selectedDims }) => {
      const limit = 1;
      if (selectedDims.length > 0 && !hasTimeBucket(selectedDims[0].name)) {
        return { isReached: true, message: 'Only time-based dimensions are allowed' };
      }
      return { isReached: selectedDims.length >= limit, message: getDimsDisabledText(limit) };
    },
  },
  [TileSubType.Tile]: {
    metrics: () => 1,
    dims: () => 0,
  },
} as const;
